/**
 * PrayerRequestsBlock — Standard plan
 * Shows incoming prayer requests with status workflow (new → prayed → archived).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Heart, Archive, Check, Trash2, AlertCircle } from "lucide-react";

const STATUS_TABS = [
  { id: "new", label: "New", icon: Heart, tone: "text-[#D4AF37]" },
  { id: "prayed", label: "Prayed", icon: Check, tone: "text-emerald-300" },
  { id: "archived", label: "Archived", icon: Archive, tone: "text-neutral-400" },
];

export default function PrayerRequestsBlock({ churchId }) {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("new");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/church-builder/churches/${churchId}/prayer-requests`, { params: { status: tab } });
      setItems(data);
    } catch { toast.error("Could not load prayer requests"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (churchId) load(); /* eslint-disable-next-line */ }, [churchId, tab]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/church-builder/churches/${churchId}/prayer-requests/${id}`, { status });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this prayer request permanently?")) return;
    try {
      await api.delete(`/church-builder/churches/${churchId}/prayer-requests/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="cb-prayer-block" className="luxury-card rounded-xl p-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-serif-display text-2xl">Prayer Requests</div>
          <div className="text-xs text-neutral-400 mt-1">Inbox for submissions from your public site.</div>
        </div>
        <div className="inline-flex border border-white/10 rounded-full p-1">
          {STATUS_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`cb-prayer-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 text-[10px] tracking-widest uppercase rounded-full inline-flex items-center gap-1.5 ${tab === t.id ? "bg-[#D4AF37] text-black" : "text-neutral-400"}`}>
                <Icon size={11} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {loading ? <Loader2 className="animate-spin text-[#D4AF37]" size={16} /> :
          items.length === 0 ? <div className="text-xs text-neutral-500 py-6">No {tab} requests.</div> :
          items.map((p) => (
            <div key={p.id} data-testid={`cb-prayer-row-${p.id}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white flex items-center gap-2">
                    {p.is_anonymous ? <span className="italic text-neutral-400">Anonymous</span> : <span>{p.name}</span>}
                    {p.is_urgent && <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-red-300 border border-red-500/40 bg-red-900/20 px-2 py-0.5 rounded"><AlertCircle size={10} /> Urgent</span>}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{!p.is_anonymous && p.email ? p.email : ""} {p.phone ? `· ${p.phone}` : ""} · {new Date(p.created_at).toLocaleString()}</div>
                  <p className="text-neutral-200 text-sm mt-2 whitespace-pre-wrap leading-relaxed">{p.request}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {p.status !== "prayed" && <button data-testid={`cb-prayer-pray-${p.id}`} onClick={() => setStatus(p.id, "prayed")} title="Mark as prayed" className="text-emerald-400 hover:opacity-80 p-2"><Check size={14} /></button>}
                  {p.status !== "archived" && <button data-testid={`cb-prayer-archive-${p.id}`} onClick={() => setStatus(p.id, "archived")} title="Archive" className="text-neutral-400 hover:text-[#D4AF37] p-2"><Archive size={14} /></button>}
                  <button data-testid={`cb-prayer-delete-${p.id}`} onClick={() => remove(p.id)} title="Delete" className="text-neutral-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}
